import Box from '@mui/material/Box';
import React from 'react';

import { FreightCarrierSection } from './FreightCarrierSection';
import { FreightDeliveryOptions } from './FreightDeliveryOptions';
import { FreightDestinationAddressSection } from './FreightDestinationAddressSection';
import { FreightOrderActions } from './FreightOrderActions';
import { FreightQuoteModal } from './FreightQuoteModal';
import { IntegratedFreightShippingProps } from './IntegratedFreightShippingFragment.props';
import { useIntegratedFreightShipping } from './useIntegratedFreightShipping';
import { Loader, LoaderColorProps } from '../../../../../components/atoms/Loader';
import { SectionLabel } from '../../../../../components/atoms/SectionLabel';
import { TextField } from '../../../../../components/atoms/TextField';
import { ShipmentPackagesList } from '../../../../../components/molecules/ShipmentPackagesList';
import { CUSTOM_PACKAGE_TYPES } from '../../../../../constants/shipment';

export const IntegratedFreightShipping: React.FC<IntegratedFreightShippingProps> = ({
  couriers,
  shipment,
  methodToggle,
}) => {
  const { state, derived, actions } = useIntegratedFreightShipping({ shipment, couriers });
  const { integratedFreightShippingData: data, setIntegratedFreightShippingData: setData } = state;

  // Turning Custom Address on clears whatever structure address was selected;
  // turning it off restores the selected one. Unchanged from before the rebuild.
  const handleCustomAddressChange = (checked: boolean) => {
    setData({ ...data, isCustomAddress: checked });
    if (checked) {
      state.setDestinationAddress(undefined);
    } else if (state.selectedOption && derived.destinationAddressdata) {
      const match = derived.destinationAddressdata.find(
        (item) => item.id === parseInt(state.selectedOption as string, 10)
      );
      if (match) {
        state.setDestinationAddress(match.value);
      }
    }
    state.setAddressForm([]);
    state.setStructureAddressDataset([]);
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <FreightCarrierSection
        leadingSlot={methodToggle}
        trailingSlot={
          <FreightOrderActions
            ordersData={derived.ordersData}
            hasSelectedPackages={data.selectedPackages.length > 0}
            toastMessageKey={String(state.freightActionCount)}
            onSaveOrder={actions.onSaveOrder}
            onArchiveOrder={actions.onArchiveOrder}
            onArchiveReturnOrder={actions.onArchiveReturnOrder}
            onReturnOrder={actions.onReturnOrder}
            onQuote={actions.onQuote}
          />
        }
        couriers={state.freightEnabledCouriers}
        selectedCourierId={data.shippingProviderId}
        onSelectCourier={(shippingProviderId) =>
          setData({ ...data, serviceType: '', shippingProviderId })
        }
        serviceTypes={state.serviceTypes}
        serviceType={data?.serviceType}
        onSelectServiceType={(serviceType) => setData({ ...data, serviceType })}
      />

      <Box>
        <SectionLabel>Packages</SectionLabel>
        <ShipmentPackagesList
          freightTypes={state.freightPackageTypes}
          customPackageTypes={CUSTOM_PACKAGE_TYPES}
          onChange={actions.onPackageSelect}
        />
      </Box>

      <FreightDeliveryOptions
        isSafeDrop={data?.isSafeDrop}
        onSafeDropChange={(isSafeDrop) => setData({ ...data, isSafeDrop })}
        isAuthToLeave={data?.isAuthToLeave}
        onAuthToLeaveChange={(isAuthToLeave) => setData({ ...data, isAuthToLeave })}
        isCustomAddress={data?.isCustomAddress}
        onCustomAddressChange={handleCustomAddressChange}
      />

      <Box>
        <SectionLabel>Destination Address</SectionLabel>
        <FreightDestinationAddressSection
          isCustomAddress={!!data?.isCustomAddress}
          destinationAddressOptions={state.destinationAddressOptions}
          selectedOption={state.selectedOption}
          onSelectOption={(value) => state.setSelectedOption(value)}
          addressData={state.addressData}
          editAddressData={state.editAddressData}
          structureAddressDataset={state.structureAddressDataset}
          setStructureAddressDataset={state.setStructureAddressDataset}
          setEditAddressData={state.setEditAddressData}
          setAddressData={state.setAddressData}
          addressForm={state.addressForm}
          setAddressForm={state.setAddressForm}
        />
      </Box>

      {/* No section label — the field's own label already says what it is. */}
      <TextField
        label="Delivery note"
        fullWidth
        multiline
        rows={3}
        value={data?.deliveryNote}
        onChange={(deliveryNote) => setData({ ...data, deliveryNote })}
      />

      <FreightQuoteModal
        open={state.openModal}
        onClose={() => state.setOpenModal(false)}
        quoteData={state.quoteData}
      />

      {derived.isPending && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => `${theme.palette.background.paper}CC`,
            zIndex: 2,
          }}
        >
          <Loader color={LoaderColorProps.Primary} />
        </Box>
      )}
    </Box>
  );
};
